import React, { useState, useEffect } from 'react';
import { Paper, TableBody, TableCell, TableHead, TableRow, Typography, TablePagination, Box, Chip, TextField, Button, MenuItem, Grid, Stack, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Table } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import axios from '../../api/client';
import { formatAmount } from '../../utils/money';
import { format } from 'date-fns';
import ClearIcon from '@mui/icons-material/Clear';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const JournalEntries = () => {
  const [entries, setEntries] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, reset,
    rows: visibleEntries, resultCount, totalCount
  } = useTableSearch(entries, {
    searchFields: ['memo', 'sourceType', 'property.propertyName'],
    filters: {}
  });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  // filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [memo, setMemo] = useState('');
  const [account, setAccount] = useState('');
  const [accounts, setAccounts] = useState([]);

  // A manual entry. There was no way to post one: the screen was read-only, so a
  // misposting could only be corrected in the database, and 3100 Opening Balance Equity
  // could never be cleared into real equity. Balanced before it can be saved, which the
  // server enforces again — this is so the person typing it sees the difference as they go.
  const blankLine = () => ({ accountId: '', debit: '', credit: '', memo: '' });
  const [newOpen, setNewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ entryDate: new Date().toISOString().slice(0, 10), memo: '', lines: [blankLine(), blankLine()] });

  const setLine = (i, patch) => setDraft((d) => ({ ...d, lines: d.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const addLine = () => setDraft((d) => ({ ...d, lines: [...d.lines, blankLine()] }));
  const removeLine = (i) => setDraft((d) => ({ ...d, lines: d.lines.length > 2 ? d.lines.filter((_, j) => j !== i) : d.lines }));
  const totalDebit = draft.lines.reduce((t, l) => t + (parseFloat(l.debit) || 0), 0);
  const totalCredit = draft.lines.reduce((t, l) => t + (parseFloat(l.credit) || 0), 0);
  const difference = Math.round((totalDebit - totalCredit) * 100) / 100;
  const canSave = draft.memo.trim() && draft.lines.every((l) => l.accountId && ((parseFloat(l.debit) || 0) > 0) !== ((parseFloat(l.credit) || 0) > 0)) && Math.abs(difference) < 0.01 && totalDebit > 0;

  const saveEntry = async () => {
    setSaving(true);
    try {
      await axios.post('/accounting/journal-entries', {
        entryDate: draft.entryDate,
        memo: draft.memo,
        lines: draft.lines.map((l) => ({ accountId: l.accountId, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0, memo: l.memo }))
      });
      toast.success('Journal entry posted');
      setNewOpen(false);
      setDraft({ entryDate: new Date().toISOString().slice(0, 10), memo: '', lines: [blankLine(), blankLine()] });
      applyFilters(page, pageSize);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not post the entry');
    } finally {
      setSaving(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const { data } = await axios.get('/accounting/accounts');
      setAccounts(data || []);
    } catch {/* ignore */}
  };

  const fetchJournalEntries = async (pg = page, psize = pageSize, filters = {}) => {
    setLoading(true);
    try {
      const params = { page: pg + 1, pageSize: psize };
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.memo) params.memo = filters.memo;
      if (filters.account) params.account = filters.account;
      const { data } = await axios.get('/accounting/journal-entries', { params });
      setEntries(data.entries);
      setTotal(data.total);
    } catch (e) {
      // Could add toast if desired
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccounts(); }, []);
  useEffect(() => {
    applyFilters(0, pageSize);
    // Paging only. applyFilters is redefined every render and is declared below this
    // effect, so it cannot be named here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const applyFilters = (pg = page, psize = pageSize) => {
    fetchJournalEntries(pg, psize, { startDate, endDate, memo, account });
  };
  const clearFilters = () => {
    setStartDate(''); setEndDate(''); setMemo(''); setAccount(''); setPage(0);
    setTimeout(() => applyFilters(0, pageSize), 0);
  };

  const handleChangePage = (evt, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = evt => {
    setPageSize(parseInt(evt.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Journal Entries</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>New Entry</Button>
      </Box>
      <Box mb={2} component={Stack} spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems="center">
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Memo"
          value={memo}
          onChange={e => setMemo(e.target.value)}
          size="small"
        />
        <TextField
          select
          label="Account"
          value={account}
          onChange={e => setAccount(e.target.value)}
          size="small"
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Accounts</MenuItem>
          {accounts.map(acc => (
            <MenuItem key={acc._id} value={acc._id}>
              {acc.code} - {acc.name}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={() => { setPage(0); applyFilters(0, pageSize); }}>Apply</Button>
        <Button variant="outlined" onClick={clearFilters} color="secondary" endIcon={<ClearIcon />}>Clear</Button>
      </Box>
      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Memo or source"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
      />
      <ScrollTable size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Memo</TableCell>
              <TableCell>Account</TableCell>
              <TableCell align="right">Debit</TableCell>
              <TableCell align="right">Credit</TableCell>
              <TableCell>Line Memo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && entries.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center">No journal entries found.</TableCell></TableRow>
            )}
            {visibleEntries.map((entry, i) => (
              entry.lines.length > 0 ? (
                entry.lines.map((line, idx) => (
                  <TableRow key={`${entry._id}-${idx}`}
                    sx={{
                      ...(idx === 0 ? { borderTop: '2px solid #eee' } : {}),
                      // Dimmed, the way the account ledger dims them: the figures are still
                      // on the page because the books are append-only, but they no longer
                      // stand, and a reader scanning the journal must be able to tell.
                      ...(entry.reversedByEntryId ? { opacity: 0.65 } : {})
                    }}>
                    {idx === 0 && (
                      <>
                        <TableCell rowSpan={entry.lines.length} sx={{ verticalAlign: 'top' }}>
                          {format(new Date(entry.entryDate), 'yyyy-MM-dd')}
                        </TableCell>
                        <TableCell rowSpan={entry.lines.length} sx={{ verticalAlign: 'top' }}>
                          {entry.memo || '-'}
                          {/* The route has always sent both flags; the screen never showed
                              them, so a reversed entry looked identical to a live one. */}
                          {entry.reversedByEntryId && (
                            <Chip size="small" label="Reversed" color="warning" variant="outlined" sx={{ ml: 1 }} />
                          )}
                          {entry.reversesEntryId && (
                            <Chip size="small" label="Reversal" color="default" variant="outlined" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                      </>
                    )}
                    {idx !== 0 && <></>}
                    <TableCell>
                      {line.account ? (
                        <Box display="flex" gap={1} alignItems="center">
                          <Chip size="small" label={line.account.code} color="info" sx={{ fontWeight: 600 }}/>
                          <span>{line.account.name}</span>
                        </Box>
                      ) : <span>-</span>}
                    </TableCell>
                    <TableCell align="right">{line.debit > 0 ? formatAmount(line.debit) : ''}</TableCell>
                    <TableCell align="right">{line.credit > 0 ? formatAmount(line.credit) : ''}</TableCell>
                    <TableCell>{line.memo || '-'}</TableCell>
                  </TableRow>
                ))
              ) : null
            ))}
          </TableBody>
        </ScrollTable>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />

      <Dialog open={newOpen} onClose={() => !saving && setNewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Journal Entry</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={4}>
              <TextField id="je-date" label="Date" type="date" fullWidth size="small" value={draft.entryDate} onChange={(e) => setDraft({ ...draft, entryDate: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField id="je-memo" label="Memo — what this entry is for" fullWidth size="small" required value={draft.memo} onChange={(e) => setDraft({ ...draft, memo: e.target.value })} />
            </Grid>
          </Grid>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 220 }}>Account</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Debit</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Credit</TableCell>
                <TableCell>Line memo</TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {draft.lines.map((line, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <TextField id={`je-line-${i}-account`} select fullWidth size="small" value={line.accountId} onChange={(e) => setLine(i, { accountId: e.target.value })}>
                      {accounts.map((a) => <MenuItem key={a._id} value={a._id}>{a.code} — {a.name}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell align="right">
                    <TextField id={`je-line-${i}-debit`} type="number" size="small" inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }} value={line.debit} onChange={(e) => setLine(i, { debit: e.target.value, credit: e.target.value ? '' : line.credit })} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField id={`je-line-${i}-credit`} type="number" size="small" inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }} value={line.credit} onChange={(e) => setLine(i, { credit: e.target.value, debit: e.target.value ? '' : line.debit })} />
                  </TableCell>
                  <TableCell>
                    <TextField id={`je-line-${i}-memo`} fullWidth size="small" value={line.memo} onChange={(e) => setLine(i, { memo: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removeLine(i)} disabled={draft.lines.length <= 2} aria-label="Remove line"><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell><Button size="small" startIcon={<AddIcon />} onClick={addLine}>Add line</Button></TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(totalDebit)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(totalCredit)}</TableCell>
                <TableCell colSpan={2} sx={{ color: Math.abs(difference) < 0.01 ? 'success.main' : 'error.main' }}>
                  {Math.abs(difference) < 0.01 ? 'Balanced' : `Out of balance by ${formatAmount(difference)}`}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveEntry} disabled={!canSave || saving}>Post Entry</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default JournalEntries;
